import { gql } from '@apollo/client'; // eslint-disable-line
import axios from 'axios';
import HttpStatus from 'http-status-codes';
import S3 from 'aws-sdk/clients/s3';

import { deleteObjectsInS3, getTokenPromise } from '..';

interface DeleteAudioFromSongInput {
    bucketName: string;
    bucketRegion: string;
    identityPoolId: string;
    getToken: GetToken;
    trqkAudioId: string;
    trqkDirectoryUrl: string;
    trqkSongId: string;
}

/**
 * Delete the trqkAudio from a song, in three steps:
 * 1. Fetch trqkAudio by id to get the asset_url
 * 2. Delete trqkAudio
 * 3. Use the asset_url to delete the audio file in s3
 * @param input - {@link DeleteAudioFromSongInput}
 * @returns details of the operations in a {@link DeleteAudioFromSongResult}
 */
export const deleteAudioFromSong = async ({
    bucketName,
    bucketRegion,
    identityPoolId,
    getToken,
    trqkAudioId,
    trqkDirectoryUrl,
    trqkSongId,
}: DeleteAudioFromSongInput): Promise<DeleteAudioFromSongResult> => {
    // 1. Fetch trqkAudio to get asset_url

    const token1 = await getTokenPromise(getToken);

    const getTrqkAudioResponse = await axios({
        method: 'post',
        url: trqkDirectoryUrl,
        headers: {
            Authorization: token1,
        },
        // throw an error for non-200 status
        validateStatus: (status: number): boolean => status === HttpStatus.OK,
        data: {
            query: `
                query getTrqkAudio(
                    $id: ID!
                ) {
                    getTrqkAudio(id: $id) {
                        id
                        asset_url
                    }
                }
            `,
            variables: {
                id: trqkAudioId,
            },
        },
    });

    if (getTrqkAudioResponse?.data?.errors) {
        throw new Error(
            `Error(s) in GraphQL query: \n${JSON.stringify(
                getTrqkAudioResponse.data.errors,
                null,
                4,
            )}`,
        );
    }

    // 2. Delete trqkAudio

    const token2 = await getTokenPromise(getToken);

    const deleteTrqkAudioResponse = await axios({
        method: 'post',
        url: trqkDirectoryUrl,
        headers: {
            Authorization: token2,
        },
        // throw an error for non-200 status
        validateStatus: (status: number): boolean => status === HttpStatus.OK,
        data: {
            query: `
                    mutation deleteTrqkAudio(
                        $input: DeleteTrqkAudioInput!
                    ) {
                        deleteTrqkAudio(input: $input) {
                            id
                            asset_url
                        }
                    }
                `,
            variables: {
                input: {
                    id: trqkAudioId,
                },
            },
        },
    });

    if (deleteTrqkAudioResponse?.data?.errors) {
        throw new Error(
            `Error(s) in GraphQL mutation: \n${JSON.stringify(
                deleteTrqkAudioResponse.data.errors,
                null,
                4,
            )}`,
        );
    }

    // 3. Use asset_url to delete audio from s3

    const assetUrl =
        getTrqkAudioResponse?.data?.data?.getTrqkAudio?.asset_url ?? null;

    if (assetUrl === null) {
        return {
            deleteAudioInS3Success: false,
            deleteTrqkAudioSuccess: true,
            trqkSongId,
        };
    }

    const deleteObjectsInS3Result = await deleteObjectsInS3({
        bucketName,
        bucketRegion,
        identityPoolId,
        objects: [
            {
                Key: assetUrl,
            },
        ],
    });

    const deleteTrqkAudioSuccess = (
        deleteObjectsInS3Result.Deleted ?? ([] as S3.DeletedObjects)
    ).some(
        (deletedObject: S3.DeletedObject): boolean =>
            deletedObject.Key === assetUrl,
    );

    return {
        deleteAudioInS3Success: true,
        deleteTrqkAudioSuccess,
        trqkSongId,
    };
};

export const DELETE_AUDIO_FROM_SONG = gql`
    mutation DeleteAudioFromSong(
        $bucketName: String!
        $bucketRegion: String!
        $identityPoolId: String!
        $taskId: ID!
        $trqkAudioId: String!
        $trqkDirectoryUrl: String!
        $trqkSongId: ID!
    ) {
        deleteAudioFromSong(
            bucketName: $bucketName
            bucketRegion: $bucketRegion
            identityPoolId: $identityPoolId
            taskId: $taskId
            trqkAudioId: $trqkAudioId
            trqkDirectoryUrl: $trqkDirectoryUrl
            trqkSongId: $trqkSongId
        ) @client
    }
`;
